/**
 * View 6, Key people & risk mitigation: who the organisation depends on, where
 * they sit, whether their role has a successor named, and what the room agreed
 * to do about the risk. The mitigating actions are free text today: no owner,
 * due date or status is captured against them yet.
 */
import React, { useMemo } from 'react'
import type { ReportingPerson } from '@/services/api/talentReportingService'
import { MIDYEAR_RATING_LABEL } from '@/components/performance/manager/talent-review/eduvosBlocks'
import { BarList, Panel, PeopleTable, Tile, TileRow, type PeopleColumn } from './TalentDashboardParts'
import { criticalOf } from './HipoView'
import {
  CRITICAL_NO,
  CRITICAL_YES,
  LAYER_ORDER,
  MOBILITY_ORDER,
  READINESS_ORDER,
  READINESS_SHORT,
  countBy,
  fmtPct,
  keyPeopleSummary,
  mobilityOf,
  roleOf,
  successorsFor,
} from './talentDashboardData'

const RATING_ORDER = ['Exceptional', 'Full', 'Not yet full', 'Weak', 'Too soon to rate']
const ratingOf = (p: ReportingPerson) => (p.midyear_rating ? MIDYEAR_RATING_LABEL[p.midyear_rating] || p.midyear_rating : null)

const successorSummary = (p: ReportingPerson) => {
  const parts = READINESS_ORDER.filter((r) => p.own_role_successors?.[r]).map(
    (r) => `${p.own_role_successors[r]} ${READINESS_SHORT[r].toLowerCase()}`,
  )
  return parts.length ? parts.join(', ') : 'None named'
}

export const KEY_PEOPLE_COLUMNS: PeopleColumn[] = [
  { key: 'name', label: 'Key person', render: (p) => p.name, sortValue: (p) => p.name },
  { key: 'role', label: 'Role', render: (p) => roleOf(p) || '', sortValue: (p) => roleOf(p) || '' },
  { key: 'function', label: 'Function', render: (p) => p.function || '', sortValue: (p) => p.function || '' },
  { key: 'layer', label: 'Leadership layer', render: (p) => p.leadership_layer || '', sortValue: (p) => p.leadership_layer || '' },
  { key: 'critical', label: 'Critical role', render: (p) => (p.critical_role ? 'Yes' : 'No'), sortValue: (p) => (p.critical_role ? 0 : 1) },
  { key: 'rating', label: 'Mid-year rating', render: (p) => ratingOf(p) || '', sortValue: (p) => ratingOf(p) || '' },
  { key: 'mobility', label: 'Mobility', render: (p) => mobilityOf(p) || '', sortValue: (p) => mobilityOf(p) || '' },
  { key: 'hipo', label: 'HIPO', render: (p) => (p.is_hipo ? 'Yes' : 'No'), sortValue: (p) => (p.is_hipo ? 0 : 1) },
  { key: 'successors', label: 'Successors for their role', render: successorSummary, sortValue: (p) => successorsFor(p) },
  { key: 'reason', label: 'Risk / dependency', render: (p) => p.key_person_reason || '', className: 'min-w-[12rem] whitespace-pre-line' },
  { key: 'mitigations', label: 'Mitigating action', render: (p) => p.mitigations || '', className: 'min-w-[12rem] whitespace-pre-line' },
]

export const KeyPeopleView: React.FC<{
  people: ReportingPerson[]
  onExport?: (rows: ReportingPerson[], what: string) => void
  /** Gender and race: administrators and HR administrators only. */
  showDemographics?: boolean
}> = ({ people, onExport, showDemographics = true }) => {
  const kp = useMemo(() => people.filter((p) => p.key_person), [people])
  const s = useMemo(() => keyPeopleSummary(people), [people])

  return (
    <div className="space-y-4">
      <TileRow>
        <Tile label="Key people" value={s.keyPeople} sub={`${fmtPct(s.keyPeople, people.length)} of those in scope`} />
        <Tile label="In critical roles" value={s.inCriticalRoles} sub={`${fmtPct(s.inCriticalRoles, s.keyPeople)} of key people`} />
        <Tile label="A successor named for their role" value={s.withSuccessor} sub={`${fmtPct(s.withSuccessor, s.keyPeople)} of key people`} />
        <Tile
          label="No successor named"
          value={s.withoutSuccessor}
          tone={s.withoutSuccessor ? 'warn' : 'default'}
          sub={`${fmtPct(s.withoutSuccessor, s.keyPeople)} of key people`}
        />
      </TileRow>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="By function"><BarList rows={countBy(kp, (p) => p.function)} /></Panel>
        <Panel title="By leadership layer"><BarList rows={countBy(kp, (p) => p.leadership_layer, LAYER_ORDER)} /></Panel>
        <Panel title="Critical role"><BarList rows={countBy(kp, criticalOf, [CRITICAL_YES, CRITICAL_NO])} /></Panel>
        <Panel title="By employment type"><BarList rows={countBy(kp, (p) => p.employment_category)} /></Panel>
        <Panel title="By mid-year rating"><BarList rows={countBy(kp, ratingOf, RATING_ORDER)} /></Panel>
        <Panel title="By mobility"><BarList rows={countBy(kp, mobilityOf, MOBILITY_ORDER)} /></Panel>
      </div>

      <Panel
        title="Profile"
        note={showDemographics ? "Totals only. Nobody's gender or race is shown or downloaded by name." : 'Gender and race are shown to administrators and HR administrators.'}
      >
        <div className="grid gap-6 md:grid-cols-3">
          {showDemographics && (
            <>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Gender</h3><BarList rows={countBy(kp, (p) => p.gender)} /></div>
              <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Race</h3><BarList rows={countBy(kp, (p) => p.race)} /></div>
            </>
          )}
          <div><h3 className="mb-2 text-xs font-semibold text-gray-600">Nationality</h3><BarList rows={countBy(kp, (p) => p.nationality)} max={8} /></div>
        </div>
      </Panel>

      <PeopleTable
        title="Key people and how the risk is being mitigated"
        note={`${kp.length} ${kp.length === 1 ? 'person' : 'people'}. The mitigating actions carry no owner, due date or status yet.`}
        people={kp}
        columns={KEY_PEOPLE_COLUMNS}
        onExport={onExport && (() => onExport(kp, 'Key people'))}
      />
    </div>
  )
}
